/**
 * NodeDetailPanel — card detail editor. Dialog on desktop, bottom-sheet
 * Drawer on mobile, same body either way (the established overlay pattern
 * used elsewhere in the app, e.g. AddBookModal).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Trash2, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button, Badge, cx } from '@/components/site/primitives';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMyWriting } from '@/components/myWriting';
import { useStoryBoard } from './context/StoryBoardContext';
import { NODE_TYPE_MAP, STATUS_OPTIONS } from './constants';

const FIELD_LABEL = 'text-xs font-semibold uppercase tracking-wide text-subtle';
const FIELD_CLASS = 'mt-1.5 rounded-xl border-line bg-cream';

function PanelBody({ storyId, node, onClose }) {
  const navigate = useNavigate();
  const { getStory } = useMyWriting();
  const { updateNode, deleteNode, duplicateNode, getBoard } = useStoryBoard();
  const story = getStory(storyId);
  const board = getBoard(storyId);

  const [title, setTitle] = useState(node.title);
  const [description, setDescription] = useState(node.description ?? '');
  const [role, setRole] = useState(node.metadata?.role ?? '');
  const [consequences, setConsequences] = useState(node.metadata?.consequences ?? '');

  useEffect(() => {
    // Re-sync local field state only when switching to a *different* card —
    // not on every commit of this same card, which would otherwise wipe
    // an unsaved, not-yet-blurred edit in another field (e.g. typing a new
    // Title, then blurring Role — committing Role must not clobber Title).
    setTitle(node.title);
    setDescription(node.description ?? '');
    setRole(node.metadata?.role ?? '');
    setConsequences(node.metadata?.consequences ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const config = NODE_TYPE_MAP[node.type];
  const characterOptions = board.nodes.filter((n) => n.type === 'character' && n.id !== node.id);
  const isCharacter = node.type === 'character';
  const showConsequences = node.type === 'plot' || node.type === 'event' || node.type === 'conflict';

  const commit = (updates) => updateNode(storyId, node.id, updates);

  const toggleCharacter = (characterId) => {
    const current = node.characterIds ?? [];
    const next = current.includes(characterId)
      ? current.filter((id) => id !== characterId)
      : [...current, characterId];
    commit({ characterIds: next });
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xl leading-none">{config?.emoji}</span>
          <Badge tone={config?.tone ?? 'coral'}>{config?.label}</Badge>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => { duplicateNode(storyId, node.id); onClose(); }}
            aria-label="Duplicate card"
            className="grid h-8 w-8 place-items-center rounded-full text-subtle hover:bg-cream hover:text-ink"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => { deleteNode(storyId, node.id); onClose(); }}
            aria-label="Delete card"
            className="grid h-8 w-8 place-items-center rounded-full text-subtle hover:bg-cream hover:text-coral"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-4">
        <label className={FIELD_LABEL}>Title</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => commit({ title: title.trim() || node.title })}
          className={cx(FIELD_CLASS, 'font-display text-base')}
        />
      </div>

      <div className="mt-4">
        <label className={FIELD_LABEL}>{isCharacter ? 'Short description' : 'Description / notes'}</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => commit({ description })}
          rows={3}
          className={cx(FIELD_CLASS, 'text-sm')}
        />
      </div>

      {isCharacter && (
        <div className="mt-4">
          <label className={FIELD_LABEL}>Role</label>
          <Input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            onBlur={() => commit({ metadata: { ...node.metadata, role } })}
            placeholder="e.g. Protagonist, Antagonist, Mentor…"
            className={FIELD_CLASS}
          />
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className={FIELD_LABEL}>Status</label>
          <Select value={node.status} onValueChange={(v) => commit({ status: v })}>
            <SelectTrigger className={FIELD_CLASS}><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className={FIELD_LABEL}>Chapter</label>
          <Select
            value={node.chapterId ?? '__none'}
            onValueChange={(v) => commit({ chapterId: v === '__none' ? null : v })}
          >
            <SelectTrigger className={FIELD_CLASS}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">None</SelectItem>
              {story?.chapters.map((ch) => <SelectItem key={ch.id} value={ch.id}>{ch.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {node.chapterId && story?.chapters.some((c) => c.id === node.chapterId) && (
        <button
          type="button"
          onClick={() => navigate(`/app/studio/${storyId}?chapter=${node.chapterId}`)}
          className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-coral hover:underline"
        >
          <BookOpen className="h-3.5 w-3.5" /> Open chapter in manuscript
        </button>
      )}

      {showConsequences && (
        <div className="mt-4">
          <label className={FIELD_LABEL}>Consequences</label>
          <Textarea
            value={consequences}
            onChange={(e) => setConsequences(e.target.value)}
            onBlur={() => commit({ metadata: { ...node.metadata, consequences } })}
            rows={2}
            placeholder="What does this lead to?"
            className={cx(FIELD_CLASS, 'text-sm')}
          />
        </div>
      )}

      {characterOptions.length > 0 && (
        <div className="mt-4">
          <label className={FIELD_LABEL}>{isCharacter ? 'Related characters' : 'Characters involved'}</label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {characterOptions.map((c) => {
              const active = (node.characterIds ?? []).includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCharacter(c.id)}
                  className={cx(
                    'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                    active ? 'border-coral bg-coral/10 text-coral' : 'border-line text-subtle hover:border-coral/40 hover:text-ink'
                  )}
                >
                  {c.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button variant="secondary" icon={false} onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

export default function NodeDetailPanel({ storyId, node, isOpen, onClose }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  if (!node) return null;

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[85vh] w-[92vw] max-w-lg overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">{node.title}</DialogTitle>
          <PanelBody storyId={storyId} node={node} onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[88vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">{node.title}</DrawerTitle>
        <PanelBody storyId={storyId} node={node} onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
